import { useMemo } from 'react';
import { useAuthStore } from '../stores/authStore';
import { useMenuStore, type MenuItem } from '../stores/menuStore';
import { normalisePath } from './catalog';
import { pageMenuItems } from './menuTree';

/**
 * The menu row for an address, when this person holds that page; null when they do not,
 * or while the menu has not arrived.
 *
 * For any button that goes to another page: draw it only when this returns a row, and
 * take its words from the row's own name ("Open <name>"), so a rename in the menu flows
 * through without an edit here. The address may carry a query (?tab=…); it is ignored
 * for the look-up.
 */
export function useHeldPage(path: string): MenuItem | null {
  const items = useMenuStore((s) => s.items);
  const loaded = useMenuStore((s) => s.loaded);
  const isSuperAdmin = useAuthStore((s) => s.user?.adminRole === 'super_admin');
  const selectedProjectId = useAuthStore((s) => s.selectedProjectId);
  return useMemo(() => {
    if (!loaded) return null;
    const pages = pageMenuItems(items, { isSuperAdmin, selectedProjectId });
    const wanted = normalisePath(path);
    const rows = pages.filter((m) => m.path && normalisePath(m.path) === wanted);
    // A section row and its first page can share an address (My Approvals / Approvals):
    // the page is the one a button should name, so a row with nothing under it wins.
    return rows.find((m) => !pages.some((p) => p.parent_id === m.id)) ?? rows[0] ?? null;
  }, [items, loaded, isSuperAdmin, selectedProjectId, path]);
}
